import { Checkbox, Dropdown, Menu } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { download } from "@skydio/api_util/src/common/http";

import { trackUserAction } from "utils/trackEvent";

import type { ColumnType } from "antd/lib/table";
import type { JSX } from "react";
import type { usePaginationFragmentHookType } from "react-relay/relay-hooks/usePaginationFragment";
import type { Connection } from "components/common/Pagination";

const DEFAULT_QUERY_SIZE = 100; // query in 100 record chunks

// Extend ColumnType to include export evaluation method
export interface ExportColumnType<RecordT extends object> extends ColumnType<RecordT> {
  // optionally override the title shown in paginated table upon export
  exportTitle?: string;
  // optionally hide this column from the table but allow exporting
  hideFromTable?: boolean;
  // if provided, this column is available for export
  export?: (node: RecordT) => string | number;
}

export interface ExportCSVProps<RecordT extends object> {
  paginationQuery: usePaginationFragmentHookType<any, any, any>;
  connection: Connection<RecordT> | null;
  columns: ExportColumnType<RecordT>[];
  fileName?: string;
  buttonText?: string;
}

const ExportCSVButton = <RecordT extends object>({
  paginationQuery,
  connection,
  columns,
  fileName = "skydio_data",
  buttonText = "Export CSV",
}: ExportCSVProps<RecordT>) => {
  // track whether our query has more data available
  const { hasNext, isLoadingNext, loadNext } = useMemo(() => paginationQuery, [paginationQuery]);
  const [exportRequested, setExportRequested] = useState(false);

  const defaultMenuOptions = useMemo(
    () =>
      Object.fromEntries(
        columns.map(col => [
          col.key!.toString(),
          {
            label: col.exportTitle ?? col.title!.toString(),
            selected: true,
            export: col.export,
          },
        ])
      ),
    [columns]
  );

  const [menuOptions, setMenuOptions] = useState(defaultMenuOptions);

  const updateMenu = useCallback(
    (key: string) => {
      // update selected value of this option
      menuOptions[key].selected = !menuOptions[key].selected;
      setMenuOptions(menuOptions);
    },
    [menuOptions]
  );

  useEffect(() => {
    if (exportRequested) {
      if (hasNext) {
        if (!isLoadingNext) {
          loadNext(DEFAULT_QUERY_SIZE);
        }
      } else {
        // construct a list of the values to export using the column evaluate methods
        const exportColumns = Object.values(menuOptions).filter(col => col.selected);
        const exportValues = connection
          ? connection.edges.map((node: RecordT | null) =>
              node ? exportColumns.map(col => (col.export ? col.export(node) : null)) : []
            )
          : [];

        // then compile a CSV string for exporting
        let exportString = exportColumns
          .map(col => col.label)
          .join(",")
          .concat("\n");

        exportValues.forEach(
          values => (exportString = exportString.concat(values.join(",")).concat("\n"))
        );

        const exportBlob = new Blob([exportString], { type: "text/csv" });
        const csvInMemoryURL = URL.createObjectURL(exportBlob);
        download(csvInMemoryURL, fileName + ".csv");
        URL.revokeObjectURL(csvInMemoryURL);
        setExportRequested(false);
      }
    }
  }, [
    exportRequested,
    paginationQuery,
    menuOptions,
    fileName,
    connection,
    loadNext,
    hasNext,
    isLoadingNext,
  ]);

  const exportMenu: JSX.Element = useMemo(
    () => (
      <Menu>
        {Object.keys(menuOptions).map(key => (
          <Menu.Item key={key}>
            <Checkbox defaultChecked onClick={() => updateMenu(key)}>
              {menuOptions[key].label}
            </Checkbox>
          </Menu.Item>
        ))}
      </Menu>
    ),
    [menuOptions, updateMenu]
  );

  const handleDownloadCSV = () => {
    trackUserAction("export_csv", { fileName: fileName });
    setExportRequested(true);
  };

  return (
    <Dropdown.Button
      type="ghost"
      overlay={exportMenu}
      onClick={handleDownloadCSV}
      loading={isLoadingNext} // show loading icon if query is still being run
    >
      {buttonText}
    </Dropdown.Button>
  );
};

export default ExportCSVButton;
